
import Layout from '../components/Layout';
import { ShieldAlert, ShieldCheck, Shield, Lock, Users, UserCheck, AlertTriangle, CheckCircle2, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SecurityTools = () => {
  const securityFeatures = [
    {
      icon: <ShieldCheck className="h-8 w-8 text-fembot-pink" />,
      title: "Raid Protection",
      description: "Automatically detects and prevents raids on your server, protecting your community from mass join attacks."
    },
    {
      icon: <Lock className="h-8 w-8 text-fembot-pink" />,
      title: "Verification Systems",
      description: "Set up custom verification processes to ensure only genuine users can access your server content."
    },
    {
      icon: <AlertTriangle className="h-8 w-8 text-fembot-pink" />,
      title: "Spam Detection",
      description: "Advanced algorithms to detect and automatically moderate spam messages and user behavior."
    },
    {
      icon: <UserCheck className="h-8 w-8 text-fembot-pink" />,
      title: "Role Security",
      description: "Monitor role changes and prevent unauthorized role modifications to protect your server hierarchy."
    },
    {
      icon: <Shield className="h-8 w-8 text-fembot-pink" />,
      title: "Anti-Phishing",
      description: "Automatically detects and removes phishing links to keep your community safe from scams."
    },
    {
      icon: <CheckCircle2 className="h-8 w-8 text-fembot-pink" />,
      title: "Security Audit",
      description: "Regular security audits and reports to help you maintain a secure Discord environment.",
      premium: true
    }
  ];

  return (
    <Layout>
      <div className="py-24 bg-gradient-to-b from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-block p-3 rounded-full bg-fembot-pink/10 mb-4">
              <ShieldAlert className="h-10 w-10 text-fembot-pink" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 dark:text-white">
              Advanced <span className="text-fembot-pink">Security</span> Tools
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Protect your Discord server with our comprehensive suite of security features
            </p>
            <p className="text-sm text-fembot-pink mt-3 italic">
              Some features are still in development and may not be available at this point in time
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg transition-all hover:shadow-xl hover:-translate-y-1 duration-300 relative overflow-visible">
                {feature.premium && (
                  <div className="absolute -top-3 -right-3 z-10">
                    <div className="inline-flex items-center p-1.5 rounded-full bg-gradient-to-r from-yellow-400 to-amber-600">
                      <div className="bg-white dark:bg-gray-800 rounded-full px-3 py-1">
                        <div className="flex items-center space-x-1 rounded-full text-xs font-semibold text-amber-700 dark:text-amber-400">
                          <Crown className="h-3 w-3" />
                          <span>Premium</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-fembot-pink/10 rounded-full p-3 w-16 h-16 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-white dark:bg-gray-800 rounded-xl p-8 shadow-xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-bold mb-2 dark:text-white">Enhanced Protection with Premium</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Upgrade to Premium for advanced security features, real-time monitoring, and customizable security settings.
                </p>
              </div>
              <Button 
                className="bg-gradient-to-r from-fembot-pink to-fembot-dark-pink hover:from-fembot-dark-pink hover:to-fembot-pink text-white px-6 py-3"
                onClick={() => window.location.href = '/premium'}
              >
                <ShieldCheck className="mr-2 h-5 w-5" />
                Upgrade Security
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SecurityTools;
